/**
 * Fire a callback once per mount, per key.
 *
 * The obvious implementation — a module-level `Set` of things already seen — is
 * wrong in a way that is invisible until someone reads the numbers: it would also
 * suppress a genuine second visit to the same article later in the session.
 *
 * A ref is the right scope. It survives React's double effect invocation in
 * development, because StrictMode re-runs the effect on the *same* fiber, and it
 * also survives the dashboard's sixty-second refetch, which hands the route a new
 * data object without remounting anything. Those are the same bug and this is the
 * same fix.
 *
 * `key` is null while content is loading and on the 404 and error branches, so a
 * failed open can never be counted as a view.
 */

import { useEffect, useRef } from "react";

export function useFireOnce(key: string | null, fire: () => void): void {
  const firedFor = useRef<string | null>(null);
  // Mirrored into a ref so a call site can pass an inline lambda without re-firing.
  // Updated in an effect rather than during render, which writing a ref inline
  // would be.
  const latest = useRef(fire);
  useEffect(() => {
    latest.current = fire;
  });

  useEffect(() => {
    if (key === null || firedFor.current === key) return;
    firedFor.current = key;
    latest.current();
  }, [key]);
}
